import {
  Dialog,
  type DialogProps,
  DialogContent,
  IconButton,
  DialogActions,
  Typography,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Children, useMemo } from "react";
import { mergeSx } from "#hooks/mergeSx";

export interface ModalProps extends DialogProps {
  header?: string;
  placement?: "center" | "top";
  showCloseButton?: boolean;
  onClose: NonNullable<DialogProps["onClose"]>;
  lazyLoad?: boolean;
}

export const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  sx,
  placement = "top",
  showCloseButton = true,
  maxWidth = "xl",
  children,
  onClose,
  header,
  lazyLoad = true,
  ...rest
}) => {
  const [content, actions] = useMemo(() => {
    const childArray = Children.toArray(children);
    const actionIndex = childArray.findIndex(
      (item) => (item as React.ReactElement).type === DialogActions
    );
    const actions =
      actionIndex !== -1 ? childArray.splice(actionIndex, 1) : undefined;
    return [childArray, actions];
  }, [children]);
  return (lazyLoad && rest.open) || !lazyLoad ? (
    <Dialog
      sx={mergeSx(
        {
          padding: {
            xs: 1,
            sm: 3,
          },
          "& .MuiDialog-container": {
            display: placement === "top" ? "block" : "flex", // Needed for dialog to anchor top
            "& .MuiPaper-root": {
              marginX: "auto",
              backgroundImage: (theme) =>
                theme.palette.mode === "light"
                  ? "linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0))"
                  : undefined,
            },
          },
        },
        sx
      )}
      onClose={onClose}
      maxWidth={maxWidth}
      {...rest}
    >
      {(header || showCloseButton) && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, pt: 2, width: "100%", gap: 3 }}
        >
          <Typography
            component="h6"
            variant="h6"
            sx={{ flexShrink: 1 }}
            data-testid="modal-header"
          >
            {header}
          </Typography>
          {showCloseButton && (
            <IconButton
              onClick={(e) => onClose(e, "backdropClick")}
              sx={{ p: 0 }}
            >
              <CloseIcon data-testid="CloseIcon" />
            </IconButton>
          )}
        </Stack>
      )}
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {content}
      </DialogContent>
      {actions}
    </Dialog>
  ) : null;
};
